import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Download, Share2, Check } from 'lucide-react';
import { useI18n } from '../i18n/I18nProvider';
import { useEvent, eventVars } from '../i18n/EventProvider';
import { useToast } from '../hooks/use-toast';

export default function Flyer() {
  const { t, lang } = useI18n();
  const { event, flyerUrl } = useEvent();
  const v = eventVars(event, lang);
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const onShare = async () => {
    const url = window.location.origin;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Planted Ministries', text: `${v.name} · ${v.dateLong} · ${v.time}`, url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
        toast({ title: t.flyer.toastTitle, description: t.flyer.toastText });
      }
    } catch (err) { /* ignore */ }
  };

  return (
    <section id="flyer" className="relative py-24 lg:py-32 bg-[#efe6cf]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <span className="text-[#b88a2a] text-xs uppercase tracking-wider-xtra font-medium">{t.flyer.eyebrow}</span>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#1f3a2a] mt-4 leading-[1.05]">{t.flyer.title}</h2>
          <p className="mt-6 text-[#4a5a4e] text-lg leading-relaxed">{t.flyer.sub}</p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button asChild className="bg-[#1f3a2a] hover:bg-[#2e5340] text-[#f5efe3] rounded-full h-12 px-6">
              <a href={flyerUrl} download="planted-flyer" target="_blank" rel="noreferrer" data-testid="flyer-download-btn" className="inline-flex items-center gap-2"><Download size={18} /> {t.flyer.download}</a>
            </Button>
            <Button onClick={onShare} variant="outline" className="rounded-full h-12 px-6 border-[#1f3a2a]/25 text-[#1f3a2a] hover:bg-[#1f3a2a]/5">
              {copied ? <Check size={18} className="mr-2" /> : <Share2 size={18} className="mr-2" />} {copied ? t.flyer.copied : t.flyer.share}
            </Button>
          </div>
          <div className="mt-8 inline-block border-l-2 border-[#d4a63d] pl-4 text-sm text-[#4a5a4e]">
            <strong className="text-[#1f3a2a]">{t.flyer.tipLabel}</strong> {t.flyer.tip}
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-6 bg-[#1f3a2a]/5 rounded-3xl blur-2xl"></div>
          <div className="relative bg-white rounded-3xl p-4 shadow-[0_30px_60px_-20px_rgba(31,58,42,0.25)]">
            <img src={flyerUrl} data-testid="flyer-image" alt={`${v.name} at Planted Ministries flyer — ${v.dateFull}, ${v.time}`} className="w-full h-auto rounded-2xl" />
          </div>
        </div>
      </div>
    </section>
  );
}
